import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
} from "@sportscanner/ui";
import { forgotPasswordSchema } from "@sportscanner/schemas";
import AuthLayout from "@/components/auth-layout";
import { requestPasswordReset } from "@/lib/auth-client";

export default function ForgotPasswordPage() {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const isDisabled = !email.trim() || isLoading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isDisabled) return;

    const parsed = forgotPasswordSchema.safeParse({ email: email.trim() });
    if (!parsed.success) {
      setErrorCode(parsed.error.issues[0]?.message ?? "VALIDATION_FAILED");
      return;
    }

    setIsLoading(true);
    setErrorCode(null);

    try {
      const adminUrl = import.meta.env.VITE_ADMIN_URL || window.location.origin;
      const result = await requestPasswordReset({
        email: parsed.data.email,
        redirectTo: `${adminUrl}/reset-password`,
      });

      if (result?.error) {
        const code = result.error.code;
        if (code === "USER_NOT_FOUND") {
          setErrorCode("USER_NOT_FOUND");
        } else {
          setErrorCode("INTERNAL_ERROR");
        }
        return;
      }

      setSent(true);
    } catch {
      setErrorCode("INTERNAL_ERROR");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <AuthLayout>
      <div className="space-y-6">
        {/* Back button */}
        <Link
          to="/login"
          className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.back")}
        </Link>

        {sent ? (
          /* Success state */
          <div className="space-y-4 text-center py-4">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-primary-500" />
              </div>
            </div>
            <div className="space-y-1">
              <h2 className="font-heading text-xl font-bold text-neutral-800">
                {t("auth.forgotPassword.successHeading")}
              </h2>
              <p className="text-sm text-neutral-500 leading-relaxed">
                {t("auth.forgotPassword.successMessage")}
              </p>
            </div>
            <Link
              to="/login"
              className="inline-block text-sm text-primary-600 hover:text-primary-700 transition-colors"
            >
              {t("auth.forgotPassword.backToLogin")}
            </Link>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-1">
              <h1 className="font-heading text-2xl font-bold text-neutral-800">
                {t("auth.forgotPassword.heading")}
              </h1>
              <p className="text-sm text-neutral-500 leading-relaxed">
                {t("auth.forgotPassword.description")}
              </p>
            </div>

            {errorCode && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {t(`errors.api.${errorCode}`)}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm text-neutral-600">
                {t("auth.forgotPassword.emailLabel")}
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder={t("auth.forgotPassword.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className="rounded-lg border-neutral-200 focus:border-primary-500"
              />
            </div>

            <Button
              type="submit"
              disabled={isDisabled}
              className="w-full py-3 h-auto bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("auth.forgotPassword.submitting")}
                </>
              ) : (
                t("auth.forgotPassword.submit")
              )}
            </Button>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
